import { Box, Button, FormControl, FormGroup, FormLabel, Modal, TextField, Typography } from "@mui/material"
import { useEffect, useState, useRef } from "react";
import { Html5QrcodeScanner } from "html5-qrcode";
import { getVoucherByQRCode, patchUseVoucher } from "services/VoucherService";
import { VoucherDiscountType } from "pages/voucher-management/VoucherType";
import { formatDate } from "utils/helper";
import { publishMessage } from "./ShireMessage";

type QRCodeVoucherDto = {
  id: number;
  name: string;
  description: string;
  discountType: VoucherDiscountType;
  discountValue: number;
  code: string;
  expiredAt: Date;
}

interface ScanQRCodeModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ScanQRCodeModal({ open, onClose }: ScanQRCodeModalProps) {
  const [qrCode, setQrCode] = useState<string>('');
  const [isScanning, setIsScanning] = useState(false);
  const [scannerError, setScannerError] = useState<string>('');
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const scannerContainerRef = useRef<HTMLDivElement>(null);
  const [voucher, setVoucher] = useState<QRCodeVoucherDto | null>(null);

  useEffect(() => {
    if (open) {
      setQrCode('');
      setScannerError('');
      setVoucher(null);
      // Add a small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        startScanner();
      }, 100);

      return () => {
        clearTimeout(timer);
        stopScanner();
      };
    } else {
      stopScanner();
    }

    return () => {
      stopScanner();
    };
  }, [open]);

  const startScanner = () => {
    if (scannerContainerRef.current && !scannerRef.current) {
      try {
        setIsScanning(true);
        setScannerError('');

        scannerRef.current = new Html5QrcodeScanner(
          "qr-reader",
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0
          },
          false
        );

        scannerRef.current.render(
          (decodedText) => {
            // QR code successfully scanned
            setQrCode(decodedText);
            stopScanner();
            setIsScanning(false);
            getScannedQRCodeInformation(decodedText);
          },
          (error) => {
            // Handle scan error (optional)
            console.log("QR Code scanning error:", error);
          }
        );
      } catch (error) {
        console.error("Failed to initialize scanner:", error);
        setScannerError("Không thể khởi tạo camera. Vui lòng kiểm tra quyền truy cập camera.");
        setIsScanning(false);
      }
    }
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      try {
        scannerRef.current.clear();
      } catch (error) {
        console.error("Error clearing scanner:", error);
      }
      scannerRef.current = null;
      setIsScanning(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await patchUseVoucher(qrCode);
    setVoucher(null);
    setQrCode('');
    setScannerError('');
    publishMessage({
      variant: "success",
      message: "Voucher đã được sử dụng"
    });
    onClose();
  }

  const handleManualInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQrCode(e.target.value);
  };

  const handleRescan = () => {
    setQrCode('');
    setScannerError('');
    startScanner();
  };

  const getScannedQRCodeInformation = async (code: string) => {
    const response = await getVoucherByQRCode(code);
    setVoucher(response);
    console.log(response);
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        component="form"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2
        }}
        onSubmit={handleSubmit}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>Quét QR Code</Typography>

        {/* Camera Scanner */}
        <Box sx={{ mb: 3 }}>
          <div id="qr-reader" ref={scannerContainerRef}></div>
          {isScanning && (
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'text.secondary' }}>
              Đang quét QR Code...
            </Typography>
          )}
          {scannerError && (
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'error.main' }}>
              {scannerError}
            </Typography>
          )}
        </Box>

        {/* Manual Input */}
        <FormGroup>
          <FormControl fullWidth>
            <FormLabel htmlFor="qrCode">Hoặc nhập mã QR thủ công</FormLabel>
            <TextField
              id="qrCode"
              name="qrCode"
              value={qrCode}
              onChange={handleManualInput}
              placeholder="Nhập mã QR code"
              fullWidth
            />
          </FormControl>
        </FormGroup>

        {voucher && (
          <Box sx={{ my: 2 }}>
            <Typography variant="h6">Thông tin voucher</Typography>
            <Typography variant="body1">Mã voucher: {voucher.code}</Typography>
            <Typography variant="body1">Tên voucher: {voucher.name}</Typography>
            <Typography variant="body1">Mô tả: {voucher.description}</Typography>
            <Typography variant="body1">Loại giảm giá: {voucher.discountType}</Typography>
            <Typography variant="body1">Giá trị giảm giá: {voucher.discountValue}</Typography>
            <Typography variant="body1">Ngày hết hạn: {formatDate(voucher.expiredAt)}</Typography>
          </Box>
        )}

        {/* Action Buttons */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button onClick={onClose} variant="outlined">
            Hủy
          </Button>
          <Box>
            {(qrCode || scannerError) && (
              <Button onClick={handleRescan} sx={{ mr: 1 }} variant="outlined">
                Quét lại
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!qrCode.trim()}
            >
              Xác nhận
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}